/**
 * ChartRegistry — defines all available modules for the dynamic chart builder.
 * Each module knows its API fetcher, what dimensions (group-by fields) it supports,
 * and what filters the user can apply before fetching.
 *
 * To add a new module: add one entry to CHART_REGISTRY and it appears automatically
 * in the builder's module picker without touching any other file.
 */

import { getReport, getStandards }                    from 'src/services/reference';
import { getUsers }                                    from 'src/services/userServicer';
import { getCompliance }                               from 'src/services/compliance';
import { fetchAllDataApi as fetchAllProcesses }        from 'src/services/processServices';

import { buildHeaders }                                from 'src/utils/apiUtils';

const CHART_DATA_BASE = process.env.REACT_APP_API_URL ?? '';

/**
 * Calls GET /chart-data on the backend and returns pre-aggregated groups.
 * All field names are validated server-side — no raw user input reaches the DB.
 */
export async function fetchChartRawApi(
  moduleId: string,
  filters: Record<string, string>,
  fieldIds: string[],
  limit = 500,
): Promise<{ columns: { id: string; label: string }[]; rows: Record<string, any>[] }> {
  try {
    const params = new URLSearchParams({ module: moduleId });
    if (fieldIds.length) params.set('fields', fieldIds.join(','));
    params.set('limit', String(limit));
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    const headers = await buildHeaders(CHART_DATA_BASE);
    const res = await fetch(`${CHART_DATA_BASE}/chart-data/raw?${params.toString()}`, {
      headers,
      credentials: 'include',
    });
    if (!res.ok) return { columns: [], rows: [] };
    const json = await res.json();
    return {
      columns: json?.data?.columns ?? [],
      rows:    json?.data?.rows    ?? [],
    };
  } catch {
    return { columns: [], rows: [] };
  }
}

export async function fetchChartDataApi(
  moduleId: string,
  dimensionId: string,
  filters: Record<string, string>,
): Promise<{ name: string; value: number }[]> {
  try {
    const params = new URLSearchParams({ module: moduleId, dimension: dimensionId });
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    const headers = await buildHeaders(CHART_DATA_BASE);
    const res = await fetch(`${CHART_DATA_BASE}/chart-data?${params.toString()}`, {
      headers,
      credentials: 'include',
    });
    if (!res.ok) return [];
    const json = await res.json();
    return (json?.data?.groups ?? []).map((g: any) => ({
      name:  String(g.name  ?? 'Unknown'),
      value: Number(g.count ?? 0),
    }));
  } catch {
    return [];
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Core types
// ─────────────────────────────────────────────────────────────────────────────

export type FilterType = 'select' | 'text' | 'date';

export interface FilterDef {
  field: string;
  apiParam: string;
  label: string;
  type: FilterType;
  /** Static options (for simple selects) */
  options?: { value: string; label: string }[];
  /** Async option loader — called once when the user picks this module */
  optionsFetcher?: () => Promise<{ value: string; label: string }[]>;
}

export interface DimensionDef {
  /** Dot-notation path into the returned data object, e.g. "group.name" */
  field: string;
  label: string;
}

/** A field that can be shown in raw-table mode */
export interface FieldDef {
  id: string;    // DB column name (matches backend allowedFields)
  label: string; // display label
}

export interface ModuleDef {
  id: string;
  label: string;
  icon: string;
  category: string;
  description: string;
  dimensions: DimensionDef[];
  filters: FilterDef[];
  /** Takes the current filter values and returns a flat array of raw records */
  fetcher: (filterValues: Record<string, string>) => Promise<any[]>;
  /**
   * When present, bypasses fetcher + client-side aggregation entirely.
   * Calls the backend /chart-data endpoint which does DB-level GROUP BY.
   */
  backendFetcher?: (
    filterValues: Record<string, string>,
    dimension: string,
  ) => Promise<{ name: string; value: number }[]>;
  /**
   * Fields available for raw-table mode (whitelisted by the backend).
   * When present, the "Raw Table" chart type is available for this module.
   */
  allowedFields?: FieldDef[];
  /**
   * Fetches raw rows from the backend for raw-table rendering.
   * Returns columns (label lookup) + rows (plain objects).
   */
  rawFetcher?: (
    filterValues: Record<string, string>,
    fieldIds: string[],
    limit?: number,
  ) => Promise<{ columns: { id: string; label: string }[]; rows: Record<string, any>[] }>;
}

export type DynChartType = 'donut' | 'bar-v' | 'bar-h' | 'table' | 'number' | 'raw-table';

export interface DynamicChartConfig {
  id: string;
  label: string;
  module: string;
  dimension: string;
  filters: { field: string; value: string }[];
  chartType: DynChartType;
  width: number;
  /** Field IDs to display in raw-table mode. Empty = all allowed fields. */
  selectedFields?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds a rawFetcher for frontend-fetched modules (no backend /chart-data/raw).
 * Calls the module's own fetcher, projects rows to the requested fields,
 * and returns the same { columns, rows } shape as fetchChartRawApi.
 */
function makeFrontendRawFetcher(
  fetcherFn: (f: Record<string, string>) => Promise<any[]>,
  fields: FieldDef[],
) {
  return async (
    filterValues: Record<string, string>,
    fieldIds: string[],
    limit = 500,
  ): Promise<{ columns: { id: string; label: string }[]; rows: Record<string, any>[] }> => {
    const allRows = await fetcherFn(filterValues);
    const visibleFields = fieldIds.length > 0
      ? fields.filter(f => fieldIds.includes(f.id))
      : fields;
    const columns = visibleFields.map(f => ({ id: f.id, label: f.label }));
    const rows = allRows.slice(0, limit).map(row => {
      const out: Record<string, any> = {};
      visibleFields.forEach(f => { out[f.id] = getFieldValue(row, f.id) || null; });
      return out;
    });
    return { columns, rows };
  };
}

/** Safely read a dot-notation path from an object */
export function getFieldValue(obj: any, path: string): string {
  const val = path.split('.').reduce((acc: any, k) => acc?.[k], obj);
  return val !== null && val !== undefined ? String(val) : '';
}

/** Normalize any API response shape into a flat array */
export const toArr = (res: any): any[] =>
  Array.isArray(res?.data) ? res.data
  : Array.isArray(res?.rows) ? res.rows
  : Array.isArray(res) ? res
  : [];

/** Group an array of records by a field and count occurrences */
export function aggregateByDimension(
  rows: any[],
  dimension: string,
): { name: string; value: number }[] {
  const counts: Record<string, number> = {};
  rows.forEach(row => {
    const key = getFieldValue(row, dimension) || 'Unknown';
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

// ─────────────────────────────────────────────────────────────────────────────
// Module registry — add new data sources here
// ─────────────────────────────────────────────────────────────────────────────

export const CHART_REGISTRY: ModuleDef[] = [
  // ── 1. Compliance Controls (report endpoint — richest filter support) ───────
  {
    id: 'compliance-controls',
    label: 'Compliance Controls',
    icon: '📋',
    category: 'Compliance',
    description: 'Controls and their compliance / task status — filter by standard, status, or due date',
    dimensions: [
      { field: 'compliance_status', label: 'Compliance Status' },
      { field: 'status',            label: 'Task Status' },
      { field: 'domain',            label: 'Domain' },
      { field: 'sub_domain',        label: 'Sub-Domain' },
    ],
    filters: [
      {
        field: 'standard', apiParam: 'standard', label: 'Standard', type: 'select',
        optionsFetcher: async () => {
          try {
            const list = toArr(await getStandards());
            return list.map((s: any) => ({
              value: s.id,
              label: s.name || s.reference_name || s.id,
            }));
          } catch { return []; }
        },
      },
      {
        field: 'complianceStatus', apiParam: 'complianceStatus', label: 'Compliance Status', type: 'select',
        options: [
          { value: 'Implemented',           label: 'Implemented' },
          { value: 'Partially Implemented', label: 'Partially Implemented' },
          { value: 'Not Implemented',       label: 'Not Implemented' },
        ],
      },
      { field: 'date', apiParam: 'date', label: 'Due Before', type: 'date' },
    ],
    fetcher: async (f) => {
      const res = await getReport(
        f.standard           || null,
        null, null, null,
        f.complianceStatus   || null,
        f.date               || null,
      );
      return toArr(res);
    },
    allowedFields: [
      { id: 'compliance_status', label: 'Compliance Status' },
      { id: 'status',            label: 'Task Status' },
      { id: 'domain',            label: 'Domain' },
      { id: 'sub_domain',        label: 'Sub-Domain' },
    ],
    get rawFetcher() {
      return makeFrontendRawFetcher(this.fetcher, this.allowedFields!);
    },
  },

  // ── 2. Compliance Frameworks ─────────────────────────────────────────────
  {
    id: 'compliance-frameworks',
    label: 'Compliance Frameworks',
    icon: '🏛️',
    category: 'Compliance',
    description: 'Active compliance frameworks / standards tracked in the system',
    dimensions: [
      { field: 'complianceStatus',   label: 'Framework Status' },
      { field: 'reference.name',     label: 'Standard Name' },
    ],
    filters: [
      {
        field: 'complianceStatus', apiParam: 'complianceStatus', label: 'Status', type: 'select',
        options: [
          { value: 'Active',    label: 'Active' },
          { value: 'Inactive',  label: 'Inactive' },
          { value: 'Completed', label: 'Completed' },
        ],
      },
    ],
    fetcher: async (f) => {
      const res = await getCompliance('', 500, 1);
      let rows = toArr(res);
      if (f.complianceStatus) rows = rows.filter((r: any) => r.complianceStatus === f.complianceStatus);
      return rows;
    },
    allowedFields: [
      { id: 'complianceStatus', label: 'Framework Status' },
      { id: 'reference.name',   label: 'Standard Name' },
    ],
    get rawFetcher() {
      return makeFrontendRawFetcher(this.fetcher, this.allowedFields!);
    },
  },

  // ── 3. Users ─────────────────────────────────────────────────────────────
  {
    id: 'users',
    label: 'Users',
    icon: '👥',
    category: 'People',
    description: 'Platform users — group by role, active status, or function',
    dimensions: [
      { field: 'group.name', label: 'Role / Group' },
      { field: 'active',     label: 'Active Status' },
    ],
    filters: [
      {
        field: 'active', apiParam: 'active', label: 'Status', type: 'select',
        options: [
          { value: 'true',  label: 'Active only' },
          { value: 'false', label: 'Inactive only' },
        ],
      },
    ],
    fetcher: async (f) => {
      const res = await getUsers('', 500, 1);
      let rows = toArr(res);
      if (f.active !== undefined && f.active !== '')
        rows = rows.filter((u: any) => String(u.active) === f.active);
      return rows;
    },
    allowedFields: [
      { id: 'username',   label: 'Username' },
      { id: 'email',      label: 'Email' },
      { id: 'active',     label: 'Active' },
      { id: 'group.name', label: 'Role / Group' },
    ],
    get rawFetcher() {
      return makeFrontendRawFetcher(this.fetcher, this.allowedFields!);
    },
  },

  // ── 4. Standards / Control References ────────────────────────────────────
  {
    id: 'standards',
    label: 'Standards',
    icon: '🏛️',
    category: 'Compliance',
    description: 'Compliance standards (control references) — total count or list by name/code',
    dimensions: [
      { field: 'name', label: 'Standard Name' },
      { field: 'code', label: 'Code' },
    ],
    filters: [],
    fetcher: async () => [],
    backendFetcher: (filters, dimension) => fetchChartDataApi('standards', dimension, filters),
    allowedFields: [
      { id: 'name',        label: 'Standard Name' },
      { id: 'code',        label: 'Code' },
      { id: 'description', label: 'Description' },
    ],
    rawFetcher: (filters, fieldIds, limit) => fetchChartRawApi('standards', filters, fieldIds, limit),
  },

  // ── 5. Risks (backend-aggregated) ─────────────────────────────────────────
  {
    id: 'risks',
    label: 'Risks',
    icon: '⚠️',
    category: 'Risk',
    description: 'Risk items — group by severity, approval status, or sub-process — DB-aggregated',
    dimensions: [
      { field: 'inherent_value', label: 'Severity (Inherent)' },
      { field: 'approve',        label: 'Approval Status' },
      { field: 'sub_process',    label: 'Sub Process' },
    ],
    filters: [
      {
        field: 'inherent_value', apiParam: 'inherent_value', label: 'Severity', type: 'select',
        options: [
          { value: 'High',   label: 'High' },
          { value: 'Medium', label: 'Medium' },
          { value: 'Low',    label: 'Low' },
        ],
      },
      {
        field: 'approve', apiParam: 'approve', label: 'Approved', type: 'select',
        options: [
          { value: 'true',  label: 'Approved' },
          { value: 'false', label: 'Not Approved' },
        ],
      },
    ],
    fetcher: async () => [],
    backendFetcher: (filters, dimension) => fetchChartDataApi('risks', dimension, filters),
    allowedFields: [
      { id: 'name',                     label: 'Name' },
      { id: 'code',                     label: 'Code' },
      { id: 'sub_process',              label: 'Sub Process' },
      { id: 'description',              label: 'Description' },
      { id: 'inherent_value',           label: 'Severity (Inherent)' },
      { id: 'inherent_frequency',       label: 'Inherent Frequency' },
      { id: 'inherent_financial_value', label: 'Inherent Financial Value' },
      { id: 'approve',                  label: 'Approved' },
      { id: 'created_by',               label: 'Created By' },
      { id: 'modifiedBy',               label: 'Modified By' },
    ],
    rawFetcher: (filters, fieldIds, limit) => fetchChartRawApi('risks', filters, fieldIds, limit),
  },

  // ── 6. Service Catalog (backend-aggregated) ───────────────────────────────
  {
    id: 'service-catalog',
    label: 'Service Catalog',
    icon: '🗂️',
    category: 'Services',
    description: 'Business services grouped by workflow status — DB-aggregated',
    dimensions: [
      { field: 'workflowStatus', label: 'Workflow Status' },
    ],
    filters: [
      { field: 'workflowStatus', apiParam: 'workflowStatus', label: 'Status', type: 'text' },
    ],
    fetcher: async () => [],
    backendFetcher: (filters, dimension) => fetchChartDataApi('service-catalog', dimension, filters),
    allowedFields: [
      { id: 'serviceName',    label: 'Service Name' },
      { id: 'code',           label: 'Code' },
      { id: 'workflowStatus', label: 'Workflow Status' },
    ],
    rawFetcher: (filters, fieldIds, limit) => fetchChartRawApi('service-catalog', filters, fieldIds, limit),
  },

  // ── 7. Assets / Resources (backend-aggregated) ────────────────────────────
  {
    id: 'assets',
    label: 'Assets',
    icon: '🖥️',
    category: 'Resources',
    description: 'IT and business assets grouped by status or security posture — DB-aggregated',
    dimensions: [
      { field: 'status',  label: 'Status' },
      { field: 'posture', label: 'Security Posture' },
    ],
    filters: [
      { field: 'status',  apiParam: 'status',  label: 'Status',  type: 'text' },
      {
        field: 'posture', apiParam: 'posture', label: 'Posture', type: 'select',
        options: [
          { value: 'Critical', label: 'Critical' },
          { value: 'High',     label: 'High' },
          { value: 'Medium',   label: 'Medium' },
          { value: 'Low',      label: 'Low' },
        ],
      },
    ],
    fetcher: async () => [],
    backendFetcher: (filters, dimension) => fetchChartDataApi('assets', dimension, filters),
    allowedFields: [
      { id: 'assetName', label: 'Asset Name' },
      { id: 'status',    label: 'Status' },
      { id: 'posture',   label: 'Security Posture' },
    ],
    rawFetcher: (filters, fieldIds, limit) => fetchChartRawApi('assets', filters, fieldIds, limit),
  },

  // ── 8. Processes ──────────────────────────────────────────────────────────
  {
    id: 'processes',
    label: 'Processes',
    icon: '🔄',
    category: 'Resources',
    description: 'Business processes — total count',
    dimensions: [
      { field: 'name', label: 'Process Name' },
    ],
    filters: [],
    fetcher: async (_f) => toArr(await fetchAllProcesses()),
    allowedFields: [
      { id: 'name',        label: 'Process Name' },
      { id: 'description', label: 'Description' },
      { id: 'status',      label: 'Status' },
    ],
    get rawFetcher() {
      return makeFrontendRawFetcher(this.fetcher, this.allowedFields!);
    },
  },

  // ── 9. Controls (backend-aggregated) ──────────────────────────────────────
  {
    id: 'controls',
    label: 'Controls',
    icon: '🛡️',
    category: 'Control',
    description: 'Controls grouped by type, frequency, entity level, or workflow stage — DB-aggregated',
    dimensions: [
      { field: 'type',             label: 'Control Type' },
      { field: 'frequency',        label: 'Frequency' },
      { field: 'method',           label: 'Method' },
      { field: 'entityLevel',      label: 'Entity Level' },
      { field: 'preparerStatus',   label: 'Preparer Status' },
      { field: 'checkerStatus',    label: 'Checker Status' },
      { field: 'reviewerStatus',   label: 'Reviewer Status' },
      { field: 'acceptanceStatus', label: 'Acceptance Status' },
    ],
    filters: [
      { field: 'type',        apiParam: 'type',        label: 'Type',        type: 'text' },
      { field: 'frequency',   apiParam: 'frequency',   label: 'Frequency',   type: 'text' },
      { field: 'method',      apiParam: 'method',      label: 'Method',      type: 'text' },
      { field: 'entityLevel', apiParam: 'entityLevel', label: 'Entity Level', type: 'text' },
      {
        field: 'preparerStatus', apiParam: 'preparerStatus', label: 'Preparer Status', type: 'select',
        options: [{ value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }],
      },
      {
        field: 'checkerStatus', apiParam: 'checkerStatus', label: 'Checker Status', type: 'select',
        options: [{ value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'refused', label: 'Refused' }],
      },
      {
        field: 'reviewerStatus', apiParam: 'reviewerStatus', label: 'Reviewer Status', type: 'select',
        options: [{ value: 'pending', label: 'Pending' }, { value: 'sent', label: 'Sent' }],
      },
      {
        field: 'acceptanceStatus', apiParam: 'acceptanceStatus', label: 'Acceptance', type: 'select',
        options: [{ value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'refused', label: 'Refused' }],
      },
    ],
    fetcher: async () => [],
    backendFetcher: (filters, dimension) => fetchChartDataApi('controls', dimension, filters),
    allowedFields: [
      { id: 'name',             label: 'Name' },
      { id: 'code',             label: 'Code' },
      { id: 'description',      label: 'Description' },
      { id: 'objective',        label: 'Objective' },
      { id: 'risk_response',    label: 'Risk Response' },
      { id: 'type',             label: 'Type' },
      { id: 'frequency',        label: 'Frequency' },
      { id: 'method',           label: 'Method' },
      { id: 'entityLevel',      label: 'Entity Level' },
      { id: 'keyControl',       label: 'Key Control' },
      { id: 'antiFraud',        label: 'Anti Fraud' },
      { id: 'test',             label: 'Test' },
      { id: 'where_factor',     label: 'Where Factor' },
      { id: 'timing',           label: 'Timing' },
      { id: 'preparerStatus',   label: 'Preparer Status' },
      { id: 'checkerStatus',    label: 'Checker Status' },
      { id: 'reviewerStatus',   label: 'Reviewer Status' },
      { id: 'acceptanceStatus', label: 'Acceptance Status' },
      { id: 'created_by',       label: 'Created By' },
      { id: 'modifiedBy',       label: 'Modified By' },
    ],
    rawFetcher: (filters, fieldIds, limit) => fetchChartRawApi('controls', filters, fieldIds, limit),
  },

  // ── 10. Compliances (backend-aggregated) ──────────────────────────────────
  {
    id: 'compliances',
    label: 'Compliance DB',
    icon: '✅',
    category: 'Compliance',
    description: 'Compliance frameworks grouped by status, progress, year, or quarter — DB-aggregated',
    dimensions: [
      { field: 'complianceStatus', label: 'Status' },
      { field: 'progressStatus',   label: 'Progress Status' },
      { field: 'year',             label: 'Year' },
      { field: 'quarter',          label: 'Quarter' },
      { field: 'approval_status',  label: 'Approval Status' },
    ],
    filters: [
      {
        field: 'complianceStatus', apiParam: 'complianceStatus', label: 'Status', type: 'select',
        options: [{ value: 'Published', label: 'Published' }, { value: 'Draft', label: 'Draft' }],
      },
      { field: 'progressStatus', apiParam: 'progressStatus', label: 'Progress Status', type: 'text' },
      { field: 'year',           apiParam: 'year',           label: 'Year',            type: 'text' },
      { field: 'quarter',        apiParam: 'quarter',        label: 'Quarter',         type: 'text' },
      { field: 'approval_status', apiParam: 'approval_status', label: 'Approval Status', type: 'text' },
    ],
    fetcher: async () => [],
    backendFetcher: (filters, dimension) => fetchChartDataApi('compliances', dimension, filters),
    allowedFields: [
      { id: 'code',              label: 'Code' },
      { id: 'complianceItem_en', label: 'Compliance Item (EN)' },
      { id: 'complianceItem_ar', label: 'Compliance Item (AR)' },
      { id: 'description_en',    label: 'Description (EN)' },
      { id: 'complianceStatus',  label: 'Status' },
      { id: 'progress',          label: 'Progress %' },
      { id: 'progressStatus',    label: 'Progress Status' },
      { id: 'quarter',           label: 'Quarter' },
      { id: 'year',              label: 'Year' },
      { id: 'approval_status',   label: 'Approval Status' },
    ],
    rawFetcher: (filters, fieldIds, limit) => fetchChartRawApi('compliances', filters, fieldIds, limit),
  },

  // ── 11. Documents (backend-aggregated) ────────────────────────────────────
  {
    id: 'documents',
    label: 'Documents',
    icon: '📁',
    category: 'Documents',
    description: 'Compliance documents grouped by category, status, or risk level — DB-aggregated',
    dimensions: [
      { field: 'category',  label: 'Category' },
      { field: 'status',    label: 'Status' },
      { field: 'riskLevel', label: 'Risk Level' },
      { field: 'framework', label: 'Framework' },
    ],
    filters: [
      // Free text: categories are the tenant's own (Configuration → Document Categories).
      { field: 'category', apiParam: 'category', label: 'Category', type: 'text' },
      {
        field: 'status', apiParam: 'status', label: 'Status', type: 'select',
        options: [
          { value: 'Draft', label: 'Draft' }, { value: 'Under Review', label: 'Under Review' },
          { value: 'Approved', label: 'Approved' }, { value: 'Published', label: 'Published' }, { value: 'Expired', label: 'Expired' },
        ],
      },
      // Free text: risk levels are the tenant's Criticalities.
      { field: 'riskLevel', apiParam: 'riskLevel', label: 'Risk Level', type: 'text' },
    ],
    fetcher: async () => [],
    backendFetcher: (filters, dimension) => fetchChartDataApi('documents', dimension, filters),
    allowedFields: [
      { id: 'title',             label: 'Title' },
      { id: 'description',       label: 'Description' },
      { id: 'category',          label: 'Category' },
      { id: 'framework',         label: 'Framework' },
      { id: 'riskLevel',         label: 'Risk Level' },
      { id: 'documentOwnerName', label: 'Document Owner' },
      { id: 'status',            label: 'Status' },
      { id: 'currentVersion',    label: 'Version' },
      { id: 'expiryDate',        label: 'Expiry Date' },
      { id: 'tags',              label: 'Tags' },
    ],
    rawFetcher: (filters, fieldIds, limit) => fetchChartRawApi('documents', filters, fieldIds, limit),
  },

  // ── 12. Incidents (backend-aggregated) ────────────────────────────────────
  {
    id: 'incidents',
    label: 'Incidents',
    icon: '🚨',
    category: 'Risk',
    description: 'Incidents grouped by status, importance, or workflow stage — DB-aggregated',
    dimensions: [
      { field: 'status',           label: 'Status' },
      { field: 'importance',       label: 'Importance' },
      { field: 'preparerStatus',   label: 'Preparer Status' },
      { field: 'checkerStatus',    label: 'Checker Status' },
      { field: 'reviewerStatus',   label: 'Reviewer Status' },
      { field: 'acceptanceStatus', label: 'Acceptance Status' },
    ],
    filters: [
      { field: 'status',     apiParam: 'status',     label: 'Status',     type: 'text' },
      { field: 'importance', apiParam: 'importance', label: 'Importance', type: 'text' },
      {
        field: 'preparerStatus', apiParam: 'preparerStatus', label: 'Preparer Status', type: 'select',
        options: [{ value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }],
      },
      {
        field: 'checkerStatus', apiParam: 'checkerStatus', label: 'Checker Status', type: 'select',
        options: [{ value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'refused', label: 'Refused' }],
      },
      {
        field: 'acceptanceStatus', apiParam: 'acceptanceStatus', label: 'Acceptance', type: 'select',
        options: [{ value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'refused', label: 'Refused' }],
      },
    ],
    fetcher: async () => [],
    backendFetcher: (filters, dimension) => fetchChartDataApi('incidents', dimension, filters),
    allowedFields: [
      { id: 'code',             label: 'Code' },
      { id: 'title',            label: 'Title' },
      { id: 'description',      label: 'Description' },
      { id: 'rootCause',        label: 'Root Cause' },
      { id: 'status',           label: 'Status' },
      { id: 'importance',       label: 'Importance' },
      { id: 'timeFrame',        label: 'Time Frame' },
      { id: 'net_loss',         label: 'Net Loss' },
      { id: 'total_loss',       label: 'Total Loss' },
      { id: 'recovery_amount',  label: 'Recovery Amount' },
      { id: 'reported_date',    label: 'Reported Date' },
      { id: 'occurrence_date',  label: 'Occurrence Date' },
      { id: 'preparerStatus',   label: 'Preparer Status' },
      { id: 'checkerStatus',    label: 'Checker Status' },
      { id: 'reviewerStatus',   label: 'Reviewer Status' },
      { id: 'acceptanceStatus', label: 'Acceptance Status' },
      { id: 'created_by',       label: 'Created By' },
    ],
    rawFetcher: (filters, fieldIds, limit) => fetchChartRawApi('incidents', filters, fieldIds, limit),
  },

  // ── 13. KRI (backend-aggregated) ──────────────────────────────────────────
  {
    id: 'kri',
    label: 'KRI',
    icon: '📊',
    category: 'Risk',
    description: 'Key Risk Indicators grouped by status, type, or frequency — DB-aggregated',
    dimensions: [
      { field: 'status',           label: 'Status' },
      { field: 'type',             label: 'Type' },
      { field: 'frequency',        label: 'Frequency' },
      { field: 'preparerStatus',   label: 'Preparer Status' },
      { field: 'checkerStatus',    label: 'Checker Status' },
      { field: 'acceptanceStatus', label: 'Acceptance Status' },
    ],
    filters: [
      { field: 'status',    apiParam: 'status',    label: 'Status',    type: 'text' },
      { field: 'type',      apiParam: 'type',      label: 'Type',      type: 'text' },
      { field: 'frequency', apiParam: 'frequency', label: 'Frequency', type: 'text' },
      {
        field: 'preparerStatus', apiParam: 'preparerStatus', label: 'Preparer Status', type: 'select',
        options: [{ value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }],
      },
      {
        field: 'checkerStatus', apiParam: 'checkerStatus', label: 'Checker Status', type: 'select',
        options: [{ value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'refused', label: 'Refused' }],
      },
      {
        field: 'acceptanceStatus', apiParam: 'acceptanceStatus', label: 'Acceptance', type: 'select',
        options: [{ value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'refused', label: 'Refused' }],
      },
    ],
    fetcher: async () => [],
    backendFetcher: (filters, dimension) => fetchChartDataApi('kri', dimension, filters),
    allowedFields: [
      { id: 'kriName',                label: 'KRI Name' },
      { id: 'code',                   label: 'Code' },
      { id: 'type',                   label: 'Type' },
      { id: 'status',                 label: 'Status' },
      { id: 'frequency',              label: 'Frequency' },
      { id: 'threshold',              label: 'Threshold' },
      { id: 'typePercentageOrFigure', label: 'Measure Type' },
      { id: 'low_from',               label: 'Low From' },
      { id: 'medium_from',            label: 'Medium From' },
      { id: 'high_from',              label: 'High From' },
      { id: 'preparerStatus',         label: 'Preparer Status' },
      { id: 'checkerStatus',          label: 'Checker Status' },
      { id: 'acceptanceStatus',       label: 'Acceptance Status' },
      { id: 'created_by',             label: 'Created By' },
      { id: 'modifiedBy',             label: 'Modified By' },
    ],
    rawFetcher: (filters, fieldIds, limit) => fetchChartRawApi('kri', filters, fieldIds, limit),
  },

  // ── 14. Action Plans (backend-aggregated) ─────────────────────────────────
  {
    id: 'actionplans',
    label: 'Action Plans',
    icon: '📋',
    category: 'Control',
    description: 'Action plans grouped by type, source, status, or completion — DB-aggregated',
    dimensions: [
      { field: 'type',             label: 'Type' },
      { field: 'from',             label: 'Source' },
      { field: 'riskType',         label: 'Risk Type' },
      { field: 'preparerStatus',   label: 'Preparer Status' },
      { field: 'checkerStatus',    label: 'Checker Status' },
      { field: 'reviewerStatus',   label: 'Reviewer Status' },
      { field: 'acceptanceStatus', label: 'Acceptance Status' },
      { field: 'done',             label: 'Completed' },
      { field: 'year',             label: 'Year' },
      { field: 'month',            label: 'Month' },
    ],
    filters: [
      { field: 'type',     apiParam: 'type',     label: 'Type',     type: 'text' },
      { field: 'from',     apiParam: 'from',     label: 'Source',   type: 'text' },
      { field: 'riskType', apiParam: 'riskType', label: 'Risk Type', type: 'text' },
      { field: 'year',     apiParam: 'year',     label: 'Year',     type: 'text' },
      {
        field: 'done', apiParam: 'done', label: 'Completed', type: 'select',
        options: [{ value: 'true', label: 'Done' }, { value: 'false', label: 'Not Done' }],
      },
      {
        field: 'preparerStatus', apiParam: 'preparerStatus', label: 'Preparer Status', type: 'select',
        options: [{ value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }],
      },
      {
        field: 'checkerStatus', apiParam: 'checkerStatus', label: 'Checker Status', type: 'select',
        options: [{ value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'refused', label: 'Refused' }],
      },
      {
        field: 'acceptanceStatus', apiParam: 'acceptanceStatus', label: 'Acceptance', type: 'select',
        options: [{ value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'refused', label: 'Refused' }],
      },
    ],
    fetcher: async () => [],
    backendFetcher: (filters, dimension) => fetchChartDataApi('actionplans', dimension, filters),
    allowedFields: [
      { id: 'type',              label: 'Type' },
      { id: 'from',              label: 'Source Module' },
      { id: 'factor',            label: 'Risk Factor' },
      { id: 'riskType',          label: 'Risk Type' },
      { id: 'control_procedure', label: 'Control Procedure' },
      { id: 'expected_cost',     label: 'Expected Cost' },
      { id: 'business_unit',     label: 'Business Unit' },
      { id: 'year',              label: 'Year' },
      { id: 'month',             label: 'Month' },
      { id: 'done',              label: 'Completed' },
      { id: 'preparerStatus',    label: 'Preparer Status' },
      { id: 'checkerStatus',     label: 'Checker Status' },
      { id: 'reviewerStatus',    label: 'Reviewer Status' },
      { id: 'acceptanceStatus',  label: 'Acceptance Status' },
      { id: 'created_by',        label: 'Created By' },
      { id: 'modifiedBy',        label: 'Modified By' },
    ],
    rawFetcher: (filters, fieldIds, limit) => fetchChartRawApi('actionplans', filters, fieldIds, limit),
  },

];
